import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from 'react-admin';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import { getCurrentCustomer } from '../../../lib/currentCustomer';
import { ClientConfigGroupAutocompleteOption, ClientConfigGroupChip } from '../../../components/clientConfig/ClientConfigGroupChip';
import authProvider from '../../../utils/authProvider';
import { typedHttpClient } from '../../../utils/httpClient';
import {
  AppDefinition,
  AppKeyDefinition,
  ChangedPreviewNode,
  areKeyDefinitionsEqual,
  buildChangedPreviewTree,
  buildConfigFromDefinitionsWithSources,
  findKeyDefinitionByPath,
  flattenChangedLeafPaths,
  getChangedObject,
  getFirstAppConfigEntry,
  hasActiveGroupSourcesAtOrUnderPath,
  isClientConfigGroupVisibleForCustomer,
  removeKeyDefinitionByPath,
  splitDefinitions,
} from '../../../lib/clientConfigHelpers';
import { useAllClientConfigGroupsQuery } from '../../../lib/clientConfigReactQuery';
import { ClientConfigChangedPreviewTree } from '../../../components/clientConfig/ClientConfigChangedPreviewTree';

type TargetType = 'customer' | 'group' | 'device';

type Option = {
  id: string;
  name: string;
  label: string;
};

type BaselineMode = 'allCustomers' | 'thisCustomer' | 'thisGroup' | 'thisDevice';
type SourcesByPath = Record<string, string[]>;

const toOption = (record: any, fallbackName: string): Option => {

  const id = record?._id || record?.id;
  const name = record?.name || record?.deviceId || fallbackName;
  return {
    id,
    name,
    label: name,
  };

};

const getDeviceOptionLabel = (record: any, assetNameById: Record<string, string>): string => {

  const assetId = record?.asset?._id || record?.asset?.id;
  if (assetId) {

    const fromRecord = typeof record?.asset?.name === 'string' ? record.asset.name.trim() : '';
    if (fromRecord) {

      return fromRecord;

    }
    const fromMap = assetNameById[assetId];
    if (fromMap) {

      return fromMap;

    }

  }
  return record?.name || record?.deviceId || 'Device';

};

const deviceRecordToOption = (record: any, assetNameById: Record<string, string>): Option => {

  const id = record?._id || record?.id;
  const label = getDeviceOptionLabel(record, assetNameById);
  return {
    id,
    name: label,
    label,
  };

};

const resourceForType = (targetType: TargetType) => {

  if (targetType === 'customer') return 'customers';
  if (targetType === 'group') return 'asset-groups';
  return 'devices';

};

const buildGroupDefinitions = (groupIds: string[]): AppDefinition[] => groupIds.map((groupId) => ({
  type: 'group',
  group: { _id: groupId, _ref: 'ClientConfigGroup' },
}));

const getFirstStringValue = (values: unknown[]): string | null => {

  for (const value of values) {

    if (typeof value === 'string' && value.trim()) {

      return value;

    }

  }
  return null;

};

const getRecordCustomerId = (record: any): string | null => {

  if (!record) {

    return null;

  }
  return getFirstStringValue([
    record?.customer?._id,
    record?.customer?.id,
    typeof record?.customer === 'string' ? record.customer : null,
    record?.customerId,
    record?.xgacCustomerId,
    record?.xgacCustomer?._id,
    record?.xgacCustomer?.id,
    record?.parentCustomer?._id,
  ]);

};

export const ClientConfigManagerPage = () => {

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const translate = useTranslate();
  const identity = useGetIdentity();
  const [searchParams] = useSearchParams();
  const isDevAdmin = authProvider.isDeveloperAdmin();
  const currentCustomer = getCurrentCustomer();
  const [lastSavedCustomer, setLastSavedCustomer] = useState<string | null>(null);

  const [customerOptions, setCustomerOptions] = useState<Option[]>([]);
  const [groupOptions, setGroupOptions] = useState<Option[]>([]);
  const [deviceOptions, setDeviceOptions] = useState<Option[]>([]);

  const {
    data: configGroupOptions = [],
    isError: configGroupsQueryFailed,
    error: configGroupsQueryError,
  } = useAllClientConfigGroupsQuery();

  useEffect(() => {

    if (!configGroupsQueryFailed || !configGroupsQueryError) {

      return;

    }
    console.error(configGroupsQueryError);
    notify('resources.config.text.defaults_update_error', { type: 'error' });

  }, [configGroupsQueryFailed, configGroupsQueryError, notify]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [selectedTargetType, setSelectedTargetType] = useState<TargetType | null>(null);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [showBaselineSettings, setShowBaselineSettings] = useState(false);
  const [baselineMode, setBaselineMode] = useState<BaselineMode>('allCustomers');

  const [baselineConfig, setBaselineConfig] = useState<Record<string, any> | null>(null);
  const [targetRecord, setTargetRecord] = useState<any>(null);
  const [initialKeyDefinitions, setInitialKeyDefinitions] = useState<AppKeyDefinition[]>([]);
  const [pendingKeyDefinitions, setPendingKeyDefinitions] = useState<AppKeyDefinition[]>([]);
  const [targetFilter, setTargetFilter] = useState<any>(undefined);
  const [initialGroupIds, setInitialGroupIds] = useState<string[]>([]);
  const [selectedConfigGroupIds, setSelectedConfigGroupIds] = useState<string[]>([]);

  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [loadingBaseline, setLoadingBaseline] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedCustomer = useMemo(
    () => customerOptions.find((option) => option.id === selectedCustomerId) || null,
    [customerOptions, selectedCustomerId],
  );
  const selectedGroup = useMemo(
    () => groupOptions.find((option) => option.id === selectedGroupId) || null,
    [groupOptions, selectedGroupId],
  );
  const selectedDevice = useMemo(
    () => deviceOptions.find((option) => option.id === selectedDeviceId) || null,
    [deviceOptions, selectedDeviceId],
  );

  const configGroupById = useMemo(() => _.keyBy(configGroupOptions, '_id'), [configGroupOptions]);

  const selectedConfigGroups = useMemo(() => configGroupOptions.filter((group) => selectedConfigGroupIds.includes(group._id)), [
    configGroupOptions,
    selectedConfigGroupIds,
  ]);
  const derivedCustomerId = useMemo(() => {

    if (selectedTargetType === 'customer') {

      return selectedTargetId;

    }
    return getRecordCustomerId(targetRecord);

  }, [selectedTargetType, selectedTargetId, targetRecord]);
  const scopedCustomerId = derivedCustomerId || getCurrentCustomer()?.value || null;
  const selectableConfigGroupOptions = useMemo(
    () => configGroupOptions.filter((group) => isClientConfigGroupVisibleForCustomer(group, scopedCustomerId)),
    [configGroupOptions, scopedCustomerId],
  );
  const canUseThisCustomer = Boolean(derivedCustomerId);
  const canUseThisGroup = Boolean(selectedTargetType === 'group' && selectedTargetId);
  const canUseThisDevice = Boolean(selectedTargetType === 'device' && selectedTargetId);
  const useEmptyBaselineForCurrentCustomer = baselineMode === 'thisCustomer' && selectedTargetType === 'customer';
  const useEmptyBaselineForCurrentGroup = baselineMode === 'thisGroup' && selectedTargetType === 'group';
  const useEmptyBaselineForCurrentDevice = baselineMode === 'thisDevice' && selectedTargetType === 'device';
  const effectiveBaselineConfig = useMemo(() => (
    (useEmptyBaselineForCurrentCustomer || useEmptyBaselineForCurrentGroup || useEmptyBaselineForCurrentDevice) ? {} : baselineConfig
  ), [
    baselineConfig,
    useEmptyBaselineForCurrentCustomer,
    useEmptyBaselineForCurrentDevice,
    useEmptyBaselineForCurrentGroup,
  ]);

  const hasGroupChanges = !_.isEqual([...initialGroupIds].sort(), [...selectedConfigGroupIds].sort());
  const hasKeyChanges = !areKeyDefinitionsEqual(initialKeyDefinitions, pendingKeyDefinitions);
  const hasPendingChanges = hasGroupChanges || hasKeyChanges;
  const addedGroupIds = useMemo(
    () => new Set(selectedConfigGroupIds.filter((groupId) => !initialGroupIds.includes(groupId))),
    [initialGroupIds, selectedConfigGroupIds],
  );
  const removedGroupIds = useMemo(
    () => new Set(initialGroupIds.filter((groupId) => !selectedConfigGroupIds.includes(groupId))),
    [initialGroupIds, selectedConfigGroupIds],
  );

  useEffect(() => {

    if (!isDevAdmin) {

      notify('resources.config.text.no_access');
      navigate(-1);

    }

  }, [isDevAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGroupsAndDevices = useCallback(async () => {

    setLoadingLists(true);
    try {

      const [assetGroups, devices] = await Promise.all([
        dataProvider.getList('asset-groups', {
          pagination: { page: 1, perPage: -1 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        }),
        dataProvider.getList('devices', {
          pagination: { page: 1, perPage: -1 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        }),
      ]);

      setGroupOptions((assetGroups.data || []).map((record: any) => toOption(record, 'Group')));
      const deviceRecords = devices.data || [];
      const assetIdsToResolve = _.uniq(
        deviceRecords
          .filter((record: any) => {

            const assetId = record?.asset?._id || record?.asset?.id;
            if (!assetId) {

              return false;

            }
            return !(typeof record?.asset?.name === 'string' && record.asset.name.trim());

          })
          .map((record: any) => record.asset._id || record.asset.id),
      );
      const assetNameById: Record<string, string> = {};
      if (assetIdsToResolve.length > 0) {

        const assetsResponse = await dataProvider.getMany('assets', { ids: assetIdsToResolve });
        (assetsResponse.data || []).forEach((asset: any) => {

          const assetId = asset?._id || asset?.id;
          if (assetId && typeof asset?.name === 'string') {

            assetNameById[assetId] = asset.name;

          }

        });

      }
      setDeviceOptions(deviceRecords.map((record: any) => deviceRecordToOption(record, assetNameById)));

    } catch (error) {

      console.error(error);
      notify('resources.config.text.defaults_update_error', { type: 'error' });

    } finally {

      setLoadingLists(false);

    }

  }, [dataProvider, notify]);

  useEffect(() => {

    const fetchCustomers = async () => {

      try {

        const customers = await dataProvider.getList('customers', {
          pagination: { page: 1, perPage: -1 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });
        setCustomerOptions((customers.data || []).map((record: any) => toOption(record, 'Customer')));

      } catch (error) {

        console.error(error);
        notify('resources.config.text.defaults_update_error', { type: 'error' });

      }

    };

    fetchCustomers();

  }, [dataProvider, notify]);

  useEffect(() => {

    const customerId = currentCustomer?.value ?? null;
    if (!customerId) {

      return;

    }
    if (lastSavedCustomer === customerId) {

      return;

    }

    const isCustomerChange = lastSavedCustomer !== null;
    if (isCustomerChange) {

      setSelectedGroupId(null);
      setSelectedDeviceId(null);
      setGroupOptions([]);
      setDeviceOptions([]);
      if (selectedTargetType === 'group' || selectedTargetType === 'device') {

        setSelectedTargetType(null);
        setSelectedTargetId(null);

      }

    }
    setLastSavedCustomer(customerId);
    fetchGroupsAndDevices();

  }, [currentCustomer?.value, identity, fetchGroupsAndDevices]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    const customerId = searchParams.get('customer');
    const groupId = searchParams.get('group');
    const deviceId = searchParams.get('device');

    if (deviceId) {

      setSelectedDeviceId(deviceId);
      setSelectedCustomerId(null);
      setSelectedGroupId(null);
      setSelectedTargetType('device');
      setSelectedTargetId(deviceId);
      return;

    }
    if (groupId) {

      setSelectedGroupId(groupId);
      setSelectedCustomerId(null);
      setSelectedDeviceId(null);
      setSelectedTargetType('group');
      setSelectedTargetId(groupId);
      return;

    }
    if (customerId) {

      setSelectedCustomerId(customerId);
      setSelectedGroupId(null);
      setSelectedDeviceId(null);
      setSelectedTargetType('customer');
      setSelectedTargetId(customerId);

    }

  }, [searchParams]);

  useEffect(() => {

    if (selectedTargetType === 'customer') {

      setBaselineMode('thisCustomer');
      return;

    }
    if (selectedTargetType === 'group') {

      setBaselineMode('thisGroup');
      return;

    }
    if (selectedTargetType === 'device') {

      setBaselineMode('thisDevice');
      return;

    }
    setBaselineMode('allCustomers');

  }, [selectedTargetType]);

  useEffect(() => {

    const loadTarget = async () => {

      if (!selectedTargetType || !selectedTargetId) {

        setTargetRecord(null);
        setInitialKeyDefinitions([]);
        setPendingKeyDefinitions([]);
        setSelectedConfigGroupIds([]);
        setInitialGroupIds([]);
        return;

      }

      setLoadingTarget(true);
      try {

        const resource = resourceForType(selectedTargetType);
        const target = await dataProvider.getOne(resource, { id: selectedTargetId });

        const appEntry = getFirstAppConfigEntry(target.data);
        const definitions = appEntry?.definitions || [];
        const { groupDefinitions, keyDefinitions } = splitDefinitions(definitions);
        const existingGroupIds = groupDefinitions.map((definition) => definition.group._id);

        setTargetRecord(target.data);
        setInitialKeyDefinitions(keyDefinitions);
        setPendingKeyDefinitions(keyDefinitions);
        setTargetFilter(appEntry?.filter);
        setSelectedConfigGroupIds(existingGroupIds);
        setInitialGroupIds(existingGroupIds);

      } catch (error) {

        console.error(error);
        notify('resources.config.text.defaults_update_error', { type: 'error' });

      } finally {

        setLoadingTarget(false);

      }

    };

    loadTarget();

  }, [selectedTargetId, selectedTargetType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    const loadBaseline = async () => {

      if (!selectedTargetType || !selectedTargetId) {

        setBaselineConfig(null);
        return;

      }
      setLoadingBaseline(true);
      try {

        let response;
        if (baselineMode === 'allCustomers') {

          response = await typedHttpClient().clientConfig.clientConfigControllerResolveAppDefaultConfigV0();

        } else if (baselineMode === 'thisCustomer') {

          if (!derivedCustomerId) {

            notify('clientConfig.manager.baseline.customerUnavailable', { type: 'warning' });
            setBaselineConfig(null);
            return;

          }
          response = await typedHttpClient().clientConfig.clientConfigControllerResolveAppCustomerConfigV0(derivedCustomerId);

        } else if (baselineMode === 'thisGroup') {

          if (!canUseThisGroup) {

            notify('clientConfig.manager.baseline.groupUnavailable', { type: 'warning' });
            setBaselineConfig(null);
            return;

          }
          response = await typedHttpClient().clientConfig.clientConfigControllerResolveAppAssetGroupConfigV0(selectedTargetId);

        } else {

          if (!canUseThisDevice) {

            notify('clientConfig.manager.baseline.deviceUnavailable', { type: 'warning' });
            setBaselineConfig(null);
            return;

          }
          response = await typedHttpClient().clientConfig.clientConfigControllerResolveAppDeviceConfigV0(selectedTargetId);

        }
        setBaselineConfig(response.data?.config || {});

      } catch (error) {

        console.error(error);
        notify('resources.config.text.defaults_update_error', { type: 'error' });

      } finally {

        setLoadingBaseline(false);

      }

    };

    loadBaseline();

  }, [
    baselineMode,
    canUseThisDevice,
    canUseThisGroup,
    derivedCustomerId,
    notify,
    selectedTargetId,
    selectedTargetType,
  ]);

  const previewData = useMemo(() => {

    if (!effectiveBaselineConfig) {

      return {
        previewConfig: {},
        sourcesByPath: {} as SourcesByPath,
        overriddenSourcesByPath: {} as SourcesByPath,
      };

    }
    if (!selectedTargetType || !selectedTargetId) {

      return {
        previewConfig: {},
        sourcesByPath: {} as SourcesByPath,
        overriddenSourcesByPath: {} as SourcesByPath,
      };

    }

    const definitions: AppDefinition[] = [
      ...pendingKeyDefinitions,
      ...buildGroupDefinitions(selectedConfigGroupIds),
    ];
    const result = buildConfigFromDefinitionsWithSources(effectiveBaselineConfig, definitions, configGroupById);
    return {
      previewConfig: result.config,
      sourcesByPath: result.sourcesByPath,
      overriddenSourcesByPath: result.overriddenSourcesByPath,
    };

  }, [
    effectiveBaselineConfig,
    selectedTargetType,
    selectedTargetId,
    pendingKeyDefinitions,
    selectedConfigGroupIds,
    configGroupById,
  ]);

  const changedKeysObject = useMemo(() => {

    if (!effectiveBaselineConfig) {

      return {};

    }
    return getChangedObject(effectiveBaselineConfig, previewData.previewConfig) || {};

  }, [effectiveBaselineConfig, previewData.previewConfig]);

  const previousPreviewData = useMemo<{
    previewConfig: Record<string, any>;
    sourcesByPath: SourcesByPath;
    overriddenSourcesByPath: SourcesByPath;
  }>(() => {

    if (!effectiveBaselineConfig || !selectedTargetType || !selectedTargetId) {

      return {
        previewConfig: {},
        sourcesByPath: {},
        overriddenSourcesByPath: {},
      };

    }
    const definitions: AppDefinition[] = [
      ...initialKeyDefinitions,
      ...buildGroupDefinitions(initialGroupIds),
    ];
    const result = buildConfigFromDefinitionsWithSources(effectiveBaselineConfig, definitions, configGroupById);
    return {
      previewConfig: result.config,
      sourcesByPath: result.sourcesByPath,
      overriddenSourcesByPath: result.overriddenSourcesByPath,
    };

  }, [
    configGroupById,
    effectiveBaselineConfig,
    initialKeyDefinitions,
    initialGroupIds,
    selectedTargetId,
    selectedTargetType,
  ]);
  const changedLeafPaths = useMemo(
    () => new Set(flattenChangedLeafPaths(changedKeysObject).map((entry) => entry.path).filter(Boolean)),
    [changedKeysObject],
  );
  const removedOnlyPaths = useMemo(
    () => Object.keys(previousPreviewData.sourcesByPath)
      .filter((path) => {

        const previousPathSources = previousPreviewData.sourcesByPath[path] || [];
        const currentPathSources = previewData.sourcesByPath[path] || [];
        const hadRemovedGroupBefore = previousPathSources.some((groupId) => removedGroupIds.has(groupId));
        return hadRemovedGroupBefore && currentPathSources.length === 0 && !changedLeafPaths.has(path);

      }),
    [changedLeafPaths, previewData.sourcesByPath, previousPreviewData.sourcesByPath, removedGroupIds],
  );
  const removedInlineKeyPaths = useMemo(() => {

    if (selectedTargetType !== 'device') {

      return [];

    }
    const pendingKeyPaths = new Set(pendingKeyDefinitions.map((definition) => definition.key));
    const removedOnlyPathSet = new Set(removedOnlyPaths);
    return initialKeyDefinitions
      .filter((definition) => !pendingKeyPaths.has(definition.key))
      .map((definition) => definition.key)
      .filter((path) => !removedOnlyPathSet.has(path));

  }, [
    initialKeyDefinitions,
    pendingKeyDefinitions,
    removedOnlyPaths,
    selectedTargetType,
  ]);
  const removedInlineKeyPathSet = useMemo(
    () => new Set(removedInlineKeyPaths),
    [removedInlineKeyPaths],
  );
  const savedChangedObject = useMemo(() => {

    if (!effectiveBaselineConfig) {

      return {};

    }
    return getChangedObject(effectiveBaselineConfig, previousPreviewData.previewConfig) || {};

  }, [effectiveBaselineConfig, previousPreviewData.previewConfig]);
  const combinedChangedObject = useMemo(() => {

    const merged = _.cloneDeep(savedChangedObject);
    const excludedPathSet = new Set([
      ...removedOnlyPaths,
      ...removedInlineKeyPaths,
    ]);
    flattenChangedLeafPaths(changedKeysObject).forEach(({ path, value }) => {

      if (!excludedPathSet.has(path)) {

        _.set(merged, path, value);

      }

    });
    excludedPathSet.forEach((path) => {

      const savedValue = _.get(savedChangedObject, path);
      const currentPreviewValue = _.get(previewData.previewConfig, path);
      const isRemovedInline = removedInlineKeyPaths.includes(path);
      const groupStillSourcesPath = hasActiveGroupSourcesAtOrUnderPath(previewData.sourcesByPath, path);

      if (isRemovedInline && groupStillSourcesPath) {

        _.set(merged, path, currentPreviewValue);
        return;

      }

      if (savedValue !== undefined) {

        _.set(merged, path, savedValue);
        return;

      }
      _.set(merged, path, _.get(previousPreviewData.previewConfig, path));

    });
    return merged;

  }, [
    changedKeysObject,
    previewData.previewConfig,
    previewData.sourcesByPath,
    previousPreviewData.previewConfig,
    removedInlineKeyPaths,
    removedOnlyPaths,
    savedChangedObject,
  ]);
  const changedTree = useMemo<ChangedPreviewNode[]>(
    () => buildChangedPreviewTree(combinedChangedObject, previewData.sourcesByPath),
    [combinedChangedObject, previewData.sourcesByPath],
  );

  const setTarget = (targetType: TargetType | null, id: string | null) => {

    setSelectedTargetType(targetType);
    setSelectedTargetId(id);

  };

  const handleCustomerChange = (value: Option | null) => {

    setSelectedCustomerId(value?.id || null);
    setSelectedGroupId(null);
    setSelectedDeviceId(null);
    setTarget(value ? 'customer' : null, value?.id || null);

  };

  const handleGroupChange = (value: Option | null) => {

    setSelectedGroupId(value?.id || null);
    setSelectedCustomerId(null);
    setSelectedDeviceId(null);
    setTarget(value ? 'group' : null, value?.id || null);

  };

  const handleDeviceChange = (value: Option | null) => {

    setSelectedDeviceId(value?.id || null);
    setSelectedCustomerId(null);
    setSelectedGroupId(null);
    setTarget(value ? 'device' : null, value?.id || null);

  };

  const handleStageInlineKeyRemoval = useCallback((path: string) => {

    setPendingKeyDefinitions((previous) => removeKeyDefinitionByPath(previous, path));

  }, []);

  const handleRestoreInlineKey = useCallback((path: string) => {

    const definition = findKeyDefinitionByPath(initialKeyDefinitions, path);
    if (!definition) {

      return;

    }
    setPendingKeyDefinitions((previous) => {

      if (previous.some((entry) => entry.key === path)) {

        return previous;

      }
      return [...previous, definition];

    });

  }, [initialKeyDefinitions]);

  const handleSave = async () => {

    if (!selectedTargetType || !selectedTargetId || !targetRecord) {

      return;

    }

    setSaving(true);
    try {

      const resource = resourceForType(selectedTargetType);
      const existingAppConfig = Array.isArray(targetRecord?.clientConfig?.app) ? targetRecord.clientConfig.app : [];
      const firstEntry = existingAppConfig[0] || { definitions: [] };

      const payloadFirstEntry = {
        ...firstEntry,
        ...(targetFilter ? { filter: targetFilter } : {}),
        definitions: [
          ...pendingKeyDefinitions,
          ...buildGroupDefinitions(selectedConfigGroupIds),
        ],
      };

      const data = {
        clientConfig: {
          ...(targetRecord?.clientConfig || {}),
          app: [payloadFirstEntry, ...existingAppConfig.slice(1)],
        },
      };

      await dataProvider.update(resource, {
        id: selectedTargetId,
        data,
        previousData: targetRecord,
      });

      setInitialGroupIds(selectedConfigGroupIds);
      setInitialKeyDefinitions(pendingKeyDefinitions);
      setTargetRecord({
        ...targetRecord,
        clientConfig: data.clientConfig,
      });
      notify('resources.app-users.text.saving_group_successful', { type: 'success' });

    } catch (error) {

      console.error(error);
      notify('resources.app-users.text.saving_group_failed', { type: 'error' });

    } finally {

      setSaving(false);

    }

  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {translate('clientConfig.manager.title')}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {loadingLists ? (
            <CircularProgress size={24} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={customerOptions}
                  value={selectedCustomer}
                  onChange={(_event, value) => handleCustomerChange(value)}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('clientConfig.manager.customer')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={groupOptions}
                  value={selectedGroup}
                  onChange={(_event, value) => handleGroupChange(value)}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('clientConfig.manager.group')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={deviceOptions}
                  value={selectedDevice}
                  onChange={(_event, value) => handleDeviceChange(value)}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('clientConfig.manager.device')}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {selectedTargetType && selectedTargetId && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {translate('clientConfig.manager.assignedGroups')}
                </Typography>
                {loadingTarget ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Autocomplete
                      multiple
                      options={selectableConfigGroupOptions}
                      value={selectedConfigGroups}
                      onChange={(_event, values) => setSelectedConfigGroupIds(values.map((value) => value._id))}
                      getOptionLabel={(option) => option.name}
                      renderTags={(value, getTagProps) => value.map((option, index) => (
                        <ClientConfigGroupChip
                          key={option._id}
                          group={option}
                          getTagProps={getTagProps}
                          index={index}
                        />
                      ))}
                      renderOption={(optionProps, option) => (
                        <ClientConfigGroupAutocompleteOption
                          optionProps={optionProps}
                          group={option}
                        />
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate('clientConfig.manager.selectGroups')}
                        />
                      )}
                    />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'end' }}>
                      <Button variant="contained" onClick={handleSave} disabled={!hasPendingChanges || saving}>
                        {translate('ra.action.save')}
                      </Button>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2,
                }}>
                  <Typography variant="h6">
                    {translate('clientConfig.manager.changedKeys')}
                  </Typography>
                  <Tooltip title={translate('clientConfig.manager.baseline.settings')}>
                    <IconButton size="small" onClick={() => setShowBaselineSettings((previous) => !previous)}>
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Collapse in={showBaselineSettings}>
                  <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="baseline-mode-label">{translate('clientConfig.manager.baseline.selectLabel')}</InputLabel>
                      <Select
                        labelId="baseline-mode-label"
                        value={baselineMode}
                        label={translate('clientConfig.manager.baseline.selectLabel')}
                        onChange={(event) => setBaselineMode(event.target.value as BaselineMode)}
                      >
                        <MenuItem value="allCustomers">{translate('clientConfig.manager.baseline.mode.allCustomers')}</MenuItem>
                        <MenuItem value="thisCustomer" disabled={!canUseThisCustomer}>
                          {translate('clientConfig.manager.baseline.mode.thisCustomer')}
                        </MenuItem>
                        <MenuItem value="thisGroup" disabled={!canUseThisGroup}>
                          {translate('clientConfig.manager.baseline.mode.thisGroup')}
                        </MenuItem>
                        <MenuItem value="thisDevice" disabled={!canUseThisDevice}>
                          {translate('clientConfig.manager.baseline.mode.thisDevice')}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Collapse>
                {loadingTarget || loadingBaseline ? (
                  <CircularProgress size={24} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {changedTree.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {translate('clientConfig.groups.preview.noChanges')}
                      </Typography>
                    )}
                    <ClientConfigChangedPreviewTree
                      roots={changedTree}
                      sourcesByPath={previewData.sourcesByPath}
                      overriddenSourcesByPath={previewData.overriddenSourcesByPath}
                      previousSourcesByPath={previousPreviewData.sourcesByPath}
                      removedGroupIds={removedGroupIds}
                      addedGroupIds={addedGroupIds}
                      removedInlineKeyPathSet={removedInlineKeyPathSet}
                      configGroupById={configGroupById}
                      selectedTargetType={selectedTargetType}
                      initialKeyDefinitions={initialKeyDefinitions}
                      onRestoreInlineKey={handleRestoreInlineKey}
                      onStageInlineKeyRemoval={handleStageInlineKeyRemoval}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );

};
